import React, { useEffect, useRef, useState } from "react";

import { useMutation, useQuery } from "@apollo/client";
import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { isBefore } from "date-fns";
import { capitalize } from "lodash";
import { makeStyles, withStyles } from "tss-react/mui";

import { Spacer } from "@/components/Spacer/Spacer";
import { graphql } from "@/gql";
import {
  AppointmentListItemFragment,
  AppointmentStatusKind,
  CreateAppointmentInput,
  UpdateAppointmentInput,
} from "@/gql/graphql";
import useDialogState from "@/hooks/useDialogState";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import useGlobalPictureInPicture from "@/hooks/usePictureInPicture";
import CreateAppointment from "@/pages/Appointments/components/CreateAppointment";
import ViewAppointmentDetails from "@/pages/Appointments/components/ViewAppointmentDetails";
import { theme } from "@/theme/customTheme";
import { colors } from "@/theme/overrides/colors";
import { format, formatISODate } from "@/utils/datetime";
import { apply } from "@/utils/functions";
import { humanize } from "@/utils/string";

import AppointmentList, {
  AppointmentListHandle,
} from "./AppointmentList/AppointmentList";
import AppointmentCard from "./components/AppointmentCard";
import AppointmentConfirmationModal from "./components/AppointmentConfirmationModal";
import CancelAppointmentModal from "./components/CancelAppointmentModal";
import EditAppointment from "./components/EditAppointment";
import FinishAppointmentModal from "./components/FinishAppointmentModal";
import SearchAppointmentListView from "./components/SearchAppointmentListView/SearchAppointmentListView";
import StatusFilterItem from "./components/StatusFilterItem";
import { ConfirmationDetails } from "./types";
import { isAppointmentFinished } from "./utils/isAppointmentFinished";

const FullGrid = withStyles(Grid, {
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
});

const ScrollableY = withStyles(Box, {
  root: {
    overflow: "hidden",
    overflowY: "auto",
    height: "100%",
  },
});

const StyledTextField = withStyles(TextField, {
  root: {
    "& .MuiInputBase-input": {
      display: "flex",
      alignItems: "center",
      height: 52,
      padding: 0,
      paddingLeft: 16,
      paddingRight: 28,
    },
  },
});

const NewAppointmentButton = withStyles(Button, (theme) => ({
  contained: {
    width: "100%",
    height: theme.typography.pxToRem(45),
    fontSize: theme.typography.pxToRem(20),
    lineHeight: theme.typography.pxToRem(18),
  },
}));

const TodayButton = withStyles(Button, (theme) => ({
  contained: {
    width: "100%",
    height: theme.typography.pxToRem(37),
    backgroundColor: "transparent",
    borderWidth: 2,
    borderStyle: "solid",
    borderColor: "#D4DBE5",
    fontWeight: 700,
    color: colors.primary,
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
}));

const useStyles = makeStyles()({
  title: {
    fontSize: 18,
    fontWeight: 700,
    marginLeft: 20,
    marginTop: 20,
  },
  header: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    justifyContent: "space-between",
    paddingLeft: 20,
    paddingRight: 20,
    paddingTop: 12,
    paddingBottom: 12,
    marginRight: 10,
    backgroundColor: colors.white,
  },
  headerFilter: {
    minWidth: 180,
  },
  dates: {
    "& .MuiInput-root": {
      fontSize: 18,
      fontWeight: 700,
      color: "#949BA8",
    },
  },
  noBorderDates: {
    border: "none",
  },
  mainContentWrapper: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    height: "100%",
    paddingTop: 20,
    paddingLeft: 20,
    paddingBottom: 20,
    overflow: "hidden",
  },
  leftContent: {
    paddingRight: 10,
  },
  rightContent: {
    backgroundColor: colors.white,
    wordWrap: "break-word",
    padding: 20,
  },

  statusMenuItem: {
    "&.Mui-selected": {
      display: "none",
    },
  },
});

const appointmentsPageQuery = graphql(`
  query AppointmentsPage(
    $clientId: ID
    $status: [AppointmentStatusKind!]
    $currentDate: ISO8601Date!
  ) {
    appointments(clientId: $clientId, status: $status) {
      date
      appointments {
        ...AppointmentListItem
        ...AppointmentPanel
      }
    }
    today: appointments(status: [UPCOMING], date: $currentDate) {
      date
      appointments {
        ...AppointmentPanel
      }
    }
  }
`);

const createAppointmentMutation = graphql(`
  mutation CreateAppointment($input: CreateAppointmentInput!) {
    createAppointment(input: $input) {
      appointment {
        ...AppointmentListItem
        ...AppointmentPanel
      }
    }
  }
`);

const updateAppointmentMutation = graphql(`
  mutation UpdateAppointment($input: UpdateAppointmentInput!) {
    updateAppointment(input: $input) {
      appointment {
        ...AppointmentListItem
        ...AppointmentPanel
      }
    }
  }
`);

const getStatus = (status: string) => {
  if (status === "ALL") return [];
  const index = Object.keys(AppointmentStatusKind).indexOf(capitalize(status));
  const filter = Object.values(AppointmentStatusKind)[index];
  const patchedFilter =
    filter === "FINISHED" ? [filter, AppointmentStatusKind.Upcoming] : filter;
  return patchedFilter;
};

const AppointmentsPage: React.FC = () => {
  const { classes, cx } = useStyles();
  const pip = useGlobalPictureInPicture();
  const [filterValue, setFilterValue] = useState<string>("ALL");
  const createAppointmentPanel = useDialogState();
  const viewAppointmentPanel = useDialogState();
  const editAppointmentPanel = useDialogState();
  const cancelModal = useDialogState();
  const finishModal = useDialogState();
  const confirmModal = useDialogState();
  const [currentSelectedAppointment, setCurrentSelectedAppointment] =
    useState<AppointmentListItemFragment>();
  const [currentSelectedStatus, setCurrentSelectStatus] =
    useState<AppointmentStatusKind>();
  const [confirmationDetails, setConfirmationDetails] =
    useState<ConfirmationDetails>();
  const [appointmentId, setAppointmentId] = useState<string>("");
  const [searchId, setSearchId] = useState<string | null>(null);
  const [searchAppointmentId, setSearchAppointmentId] =
    useState<string | null>(null);
  const snackbar = useGlobalSnackbar();
  const { loading, data } = useQuery(appointmentsPageQuery, {
    variables: {
      clientId: searchId,
      status: getStatus(filterValue),
      currentDate: formatISODate(new Date()),
    },
  });
  const [createAppointment] = useMutation(createAppointmentMutation);
  const [updateAppointment] = useMutation(updateAppointmentMutation);
  const [scrollToDate, setScrollToDate] = useState(new Date());
  const listRef = useRef<AppointmentListHandle>(null);

  useEffect(() => {
    listRef.current?.scrollToDate(scrollToDate);
  }, [scrollToDate]);

  function handleStatusFilter(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;
    setFilterValue(value);
  }
  async function handleCancelAppointment() {
    const input: UpdateAppointmentInput = {
      id: appointmentId,
      patch: {
        status: AppointmentStatusKind.Cancelled,
      },
    };
    try {
      const result = await updateAppointment({
        variables: { input },
      });
      cancelModal.hide();
      confirmModal.show();
      setConfirmationDetails({
        startDate: format(
          {
            date:
              result.data?.updateAppointment.appointment.startDate ??
              new Date(),
          },
          "dd MMMM yyyy"
        ),
        startTime: format(
          {
            timeofday:
              result.data?.updateAppointment.appointment.startTime ??
              new Date(),
          },
          "hh:mm aa"
        ),
        endTime: format(
          {
            timeofday:
              result.data?.updateAppointment.appointment.endTime ?? new Date(),
          },
          "hh:mm aa"
        ),
        fullName:
          result.data?.updateAppointment.appointment.client.profile?.fullName,
        status: result.data?.updateAppointment?.appointment?.status,
      });
    } catch (error) {
      snackbar.open(
        `There was an error in cancelling your appointment ${error}`
      );
    }
  }

  async function handleCreateAppointment(input: CreateAppointmentInput) {
    try {
      const result = await createAppointment({
        variables: { input: input },
        refetchQueries: [
          {
            query: appointmentsPageQuery,
            variables: {
              clientId: searchId,
              status: getStatus(filterValue),
              currentDate: formatISODate(new Date()),
            },
          },
        ],
      });
      setConfirmationDetails({
        startDate: format(
          {
            date:
              result.data?.createAppointment?.appointment?.startDate ??
              new Date(),
          },
          "dd MMMM yyyy"
        ),
        startTime: format(
          {
            timeofday:
              result.data?.createAppointment?.appointment?.startTime ??
              new Date(),
          },
          "hh:mm aa"
        ),
        endTime: format(
          {
            timeofday:
              result.data?.createAppointment?.appointment?.endTime ??
              new Date(),
          },
          "hh:mm aa"
        ),
        fullName:
          result.data?.createAppointment?.appointment?.client.profile?.fullName,

        status: result.data?.createAppointment?.appointment?.status,
      });
      createAppointmentPanel.hide();
      confirmModal.show();
    } catch (error) {
      snackbar.open(`There was an error in creating your appointment ${error}`);
    }
  }

  async function handleEditAppointment(input: UpdateAppointmentInput) {
    try {
      await updateAppointment({
        variables: { input: input },
      });
      editAppointmentPanel.hide();
      finishModal.hide();
      snackbar.open("Successfully saved appointment details.");
    } catch (error) {
      snackbar.open(`There was an error in updating your appointment ${error}`);
    }
  }

  async function handleChangeAppointmentStatus() {
    const cannotFinishValidation =
      currentSelectedStatus == AppointmentStatusKind.Finished &&
      isBefore(
        new Date(),
        new Date(
          `${currentSelectedAppointment?.startDate} ${currentSelectedAppointment?.endTime}`
        )
      );
    if (cannotFinishValidation) {
      snackbar.open(
        "You cannot finish a current appointment. You may cancel it instead."
      );
    } else {
      const updateInput: UpdateAppointmentInput = {
        id: currentSelectedAppointment?.id ?? "",
        patch: {
          status: currentSelectedStatus,
        },
      };
      handleEditAppointment(updateInput);
    }
  }

  function handleShowFinishAppointmentModal(
    data: AppointmentListItemFragment,
    status: AppointmentStatusKind
  ) {
    if (status == AppointmentStatusKind.Finished) {
      setCurrentSelectStatus(status);
      setCurrentSelectedAppointment(data);
      finishModal.show();
    } else {
      //Then it must be cancel
      setAppointmentId(data.id);
      cancelModal.show();
    }
  }

  function handleMenuClick(type: string, id: string) {
    setAppointmentId(id);
    switch (type) {
      case "details": {
        return viewAppointmentPanel.show();
      }
      case "edit": {
        return editAppointmentPanel.show();
      }
      case "cancel": {
        return cancelModal.show();
      }
    }
  }

  const appointmentsToday = data?.today?.[0]?.appointments ?? [];

  return (
    <>
      <Typography className={classes.title}>Appointments</Typography>
      <Grid container className={classes.mainContentWrapper}>
        <FullGrid item xs={9}>
          <Box className={classes.header}>
            <Box display="flex" alignItems="center">
              <DatePicker
                className={classes.dates}
                value={scrollToDate}
                onChange={(date) => apply(date, setScrollToDate)}
                format="d MMM, yyyy"
              />
              <Box marginLeft={5}>
                <TodayButton onClick={() => setScrollToDate(new Date())}>
                  Today
                </TodayButton>
              </Box>
            </Box>
            <Box display="flex" alignItems="center" flexWrap="wrap">
              <Box marginRight={theme.spacing(1)}>
                <SearchAppointmentListView
                  onSelectedID={setSearchId}
                  onSelectedAppointmentID={setSearchAppointmentId}
                  onClearSelected={() => setSearchId(null)}
                />
              </Box>
              <Box>
                <StyledTextField
                  select
                  SelectProps={{
                    MenuProps: {
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                    },
                  }}
                  className={classes.headerFilter}
                  value={filterValue}
                  onChange={handleStatusFilter}
                >
                  <MenuItem className={classes.statusMenuItem} value="ALL">
                    <StatusFilterItem value="all">All</StatusFilterItem>
                  </MenuItem>
                  <MenuItem
                    className={classes.statusMenuItem}
                    value={AppointmentStatusKind.Upcoming}
                  >
                    <StatusFilterItem value={AppointmentStatusKind.Upcoming}>
                      {humanize(AppointmentStatusKind.Upcoming)}
                    </StatusFilterItem>
                  </MenuItem>
                  <MenuItem
                    className={classes.statusMenuItem}
                    value={AppointmentStatusKind.Finished}
                  >
                    <StatusFilterItem value={AppointmentStatusKind.Finished}>
                      {humanize(AppointmentStatusKind.Finished)}
                    </StatusFilterItem>
                  </MenuItem>
                  <MenuItem
                    className={classes.statusMenuItem}
                    value={AppointmentStatusKind.Cancelled}
                  >
                    <StatusFilterItem value={AppointmentStatusKind.Cancelled}>
                      {humanize(AppointmentStatusKind.Cancelled)}
                    </StatusFilterItem>
                  </MenuItem>
                </StyledTextField>
              </Box>
            </Box>
          </Box>
          <ScrollableY className={cx(classes.leftContent)}>
            {loading && data == null ? (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width="100%"
                padding={14}
              >
                <CircularProgress />
              </Box>
            ) : (
              <AppointmentList
                ref={listRef}
                data={data}
                onClickMenu={handleMenuClick}
                searchAppointmentId={searchAppointmentId}
                filterValue={filterValue}
                handleShowFinishAppointmentModal={
                  handleShowFinishAppointmentModal
                }
              />
            )}
          </ScrollableY>
        </FullGrid>
        <FullGrid item xs={3}>
          <ScrollableY className={classes.rightContent}>
            <NewAppointmentButton
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={createAppointmentPanel.show}
            >
              New Appointment
            </NewAppointmentButton>
            <Spacer h={16} />
            <Stack spacing={2}>
              {appointmentsToday.map((appointment) => {
                if (
                  appointment.status === "cancelled" ||
                  isAppointmentFinished(appointment)
                )
                  return null;

                return (
                  <AppointmentCard
                    onEdit={() => {
                      setAppointmentId(appointment.id);
                      editAppointmentPanel.show();
                    }}
                    key={appointment.id}
                    id={appointment.id}
                    onClickMenu={handleMenuClick}
                    onJoin={() =>
                      pip.open(appointment.meetingDetails?.roomUrl ?? "")
                    }
                  />
                );
              })}
            </Stack>
          </ScrollableY>
        </FullGrid>
      </Grid>
      <CreateAppointment
        {...createAppointmentPanel.props}
        handleCreate={handleCreateAppointment}
      />
      <ViewAppointmentDetails
        {...viewAppointmentPanel.props}
        id={appointmentId}
        onEdit={editAppointmentPanel.show}
        onCancel={cancelModal.show}
      />
      <EditAppointment
        {...editAppointmentPanel.props}
        id={appointmentId}
        handleEdit={handleEditAppointment}
        onCancel={cancelModal.show}
      />
      <CancelAppointmentModal
        {...cancelModal.props}
        handleCancel={handleCancelAppointment}
      />

      <FinishAppointmentModal
        {...finishModal.props}
        handleFinish={handleChangeAppointmentStatus}
      />
      <AppointmentConfirmationModal
        {...confirmModal.props}
        startDate={confirmationDetails?.startDate}
        startTime={confirmationDetails?.startTime}
        endTime={confirmationDetails?.endTime}
        fullName={confirmationDetails?.fullName}
        status={confirmationDetails?.status}
      />
    </>
  );
};

export default AppointmentsPage;
